import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, onAuthStateChange, signOut } from '../supabase';
import { authenticatedFetch } from '../utils';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [agentNames, setAgentNames] = useState([]);
  const [tenantName, setTenantName] = useState('');
  const [, setTrialExpired] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [trialExpiresAt, setTrialExpiresAt] = useState(null);
  const [authError, setAuthError] = useState('');

  const handleAccountPaused = useCallback(async () => {
    await signOut();
    setAuthenticated(false);
    setUser(null);
    setRole(null);
    setAgentNames([]);
    setTrialExpired(false);
    navigate('/account-paused');
  }, [navigate]);

  const isOwner = role === 'owner';

  const fetchUserRole = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/auth/validate');
      if (response.ok) {
        const data = await response.json();
        const userWithTenant = data.user;
        setRole(userWithTenant?.role || null);
        setAgentNames(userWithTenant?.agent_names || []);
        setIsTrial(userWithTenant?.is_trial || false);
        setTrialExpiresAt(userWithTenant?.trial_expires_at || null);
        setTrialExpired(false);
        try {
          const { data: tenantData } = await supabase
            .from('tenants')
            .select('name')
            .single();
          setTenantName(tenantData?.name || '');
        } catch (e) {
          // tenant name not critical
        }
      } else if (response.status === 403) {
        try {
          const errorData = await response.json();
          if (errorData.detail && errorData.detail.account_paused) {
            await handleAccountPaused();
            return;
          }
          if (errorData.detail && errorData.detail.trial_expired) {
            setTrialExpired(true);
            setAuthenticated(false);
            setUser(null);
            navigate('/trial-expired');
            return;
          }
          if (errorData.detail && errorData.detail.pending_review) {
            navigate('/pending-review');
            return;
          }
        } catch (e) {
          // detail might be a string
        }
        setTrialExpired(false);
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  }, [navigate, handleAccountPaused]);

  const handleLogin = useCallback(async (session) => {
    if (session && session.user) {
      setAuthenticated(true);
      setUser(session.user);
      setAuthError('');
      try {
        const response = await authenticatedFetch('/auth/validate');
        if (response.status === 403) {
          const errorData = await response.json();
          if (errorData.detail && errorData.detail.account_paused) {
            await handleAccountPaused();
            return;
          }
          if (errorData.detail && errorData.detail.trial_expired) {
            await signOut();
            setAuthenticated(false);
            setUser(null);
            navigate('/trial-expired');
            return;
          }
          if (errorData.detail && errorData.detail.wrong_tenant_domain) {
            await signOut();
            setAuthenticated(false);
            setUser(null);
            setAuthError(errorData.detail.message || 'This account is not linked to this domain.');
            navigate('/login');
            return;
          }
          if (errorData.detail && errorData.detail.pending_review) {
            navigate('/pending-review');
            return;
          }
        }
      } catch (e) {
        console.error('Trial check on login error:', e);
      }
      navigate('/');
    }
  }, [navigate, handleAccountPaused]);

  const handleLogout = useCallback(async () => {
    await signOut();
    setAuthenticated(false);
    setUser(null);
    setRole(null);
    setAgentNames([]);
    setTenantName('');
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    const { data: { subscription } } = onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') {
        if (session?.user) {
          setAuthenticated(true);
          setUser(session.user);
        } else {
          setAuthenticated(false);
          setUser(null);
        }
        setAuthLoading(false);
      } else if (event === 'SIGNED_IN' && session) {
        setAuthenticated(true);
        setUser(session.user);
        if (window.location.pathname === '/login' || window.location.pathname === '/signup') {
          navigate('/');
        }
      } else if (event === 'SIGNED_OUT') {
        setAuthenticated(false);
        setUser(null);
        setRole(null);
        setAgentNames([]);
        setTenantName('');
        navigate('/');
      } else if (event === 'TOKEN_REFRESHED' && session) {
        setAuthenticated(true);
        setUser(session.user);
      } else if (event === 'USER_UPDATED' && session) {
        setAuthenticated(true);
        setUser(session.user);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [navigate]);

  const userId = user?.id;
  useEffect(() => {
    if (authenticated && userId) {
      fetchUserRole();
    }
  }, [authenticated, userId, fetchUserRole]);

  return (
    <AuthContext.Provider value={{ authenticated, user, authLoading, role, agentNames, tenantName, isTrial, trialExpiresAt, isOwner, authError, handleLogin, handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
};
